import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * The English text of the reply-to-AI-emails UI (PRD copy table included).
 *
 * Repo convention: strings live in `locales/<lang>/shipments.json` and are read
 * with `useTranslation('shipments')`. The English here is also each key's
 * default, so the UI reads the same before a language file has the key (other
 * languages fall back to English). `locales/en/shipments.json` carries the
 * same text under `emailReply`; a test keeps the two equal.
 */
export const EMAIL_REPLY_STRINGS = {
  message: 'Message',
  emailTranscript: 'Email transcript',
  aiSummary: 'AI summary:',
  send: 'Send',
  cancel: 'Cancel',
  attach: 'Attach',
  insertLink: 'Insert link',
  fromDevice: 'From device',
  fromShipmentDocuments: 'From shipment documents',
  shipmentDocuments: 'Shipment documents',
  attachSelected: 'Attach selected',
  noDocuments: 'No documents on this shipment.',
  discardTitle: 'Discard this draft?',
  discard: 'Discard',
  keepEditing: 'Keep editing',
  sendAnyway: 'Send anyway',
  fieldsNeedInput_one: '{{count}} field still needs input',
  fieldsNeedInput_other: '{{count}} fields still need input',
  drafting: 'Drafting a reply… you can start typing.',
  draftWithAi: 'Draft with AI',
  draftWithAiHint: 'Press to generate AI answer',
  draftFailed: 'Couldn’t draft a reply.',
  draftNone: 'There is no email on this thread to answer.',
  removeAttachment: 'Remove {{name}}',
  attachLimit: 'Attachments are limited to {{mb}} MB in total.',
  documentsLoadFailed: 'Could not load the shipment documents.',
  sending: 'Sending…',
  justNow: 'Just now',
  couldntSend: 'Couldn’t send',
  retry: 'Retry',
  edit: 'Edit',
  pausedTitle: 'AI replies paused on this thread',
  resumeAi: 'Resume AI',
  resumeFailed: 'Could not resume the AI. Please try again.',
  needsAttention: 'Needs attention',
  replyNeeded: 'Reply needed',
  reply: 'Reply',
  viewTranscript: 'View email transcript',
  to: 'to',
  cc: 'cc',
  subject: 'subject',
  aiEmailAgent: 'AI email agent',
} as const;

export type EmailReplyStringKey = keyof typeof EMAIL_REPLY_STRINGS;

type PluralKey = Extract<EmailReplyStringKey, `${string}_one`>;
type PlainKey = Exclude<EmailReplyStringKey, PluralKey | `${string}_other`>;

/** `t('fieldsNeedInput', n)` and `t('send')`, in the user's language. */
export function useEmailReplyT() {
  const { t } = useTranslation('shipments');

  const text = useCallback(
    (key: PlainKey, vars?: Record<string, string | number>): string =>
      t(`emailReply.${key}`, {
        defaultValue: EMAIL_REPLY_STRINGS[key],
        ...vars,
      }),
    [t]
  );

  const fieldsNeedInput = useCallback(
    (count: number): string =>
      t('emailReply.fieldsNeedInput', {
        count,
        defaultValue_one: EMAIL_REPLY_STRINGS.fieldsNeedInput_one,
        defaultValue_other: EMAIL_REPLY_STRINGS.fieldsNeedInput_other,
      }),
    [t]
  );

  return { t: text, fieldsNeedInput };
}
